// SPDX-License-Identifier: AGPL-3.0-or-later

import {ResettableStates} from '@app/features/app/state/ResettableStates';
import RuntimeConfig from '@app/features/app/state/RuntimeConfig';
import GuildMatureContentAgree from '@app/features/guild/state/GuildMatureContentAgree';
import {stripMediaProxyParams} from '@app/features/messaging/utils/MediaProxyUtils';
import {buildWireMessageNotificationBody} from '@app/features/notification/utils/MessageNotificationPreview';
import Relationships from '@app/features/relationship/state/Relationships';
import {
	EmbedMediaFlags,
	MessageAttachmentFlags,
	MessageEmbedTypes,
	MessageTypes,
} from '@fluxer/constants/src/ChannelConstants';
import {ROOM_LIMITS} from '@fluxer/constants/src/RoomCharacters';
import type {EmbedMedia, MessageEmbed} from '@fluxer/schema/src/domains/message/EmbedSchemas';
import type {Message} from '@fluxer/schema/src/domains/message/MessageResponseSchemas';
import {i18n} from '@lingui/core';
import {makeAutoObservable} from 'mobx';

export interface BubbleEmoji {
	id: string;
	name: string;
	animated: boolean;
}

export interface Bubble {
	id: string;
	text: string;
	emojis: ReadonlyArray<BubbleEmoji>;
	// The media proxy base URL of a gifv embed's preview; never a URL taken from the message text.
	gif: {url: string; width: number; height: number} | null;
	spoiler: boolean;
	expiresAt: number;
}

interface Entry {
	authorId: string;
	bubble: Bubble;
}

// Shown time including the closing fade; the component fades over the last BUBBLE_FADE_MS.
export const BUBBLE_LIFETIME_MS = 6000;
export const BUBBLE_FADE_MS = 600;
const BUBBLES_PER_PERSON = 3;
const GIF_MAX_WIDTH = 160;
const GIF_MAX_HEIGHT = 120;
// Live messages only; the slack absorbs a client clock running ahead of the server's.
const OPENED_AT_SLACK_MS = 60_000;
const NO_BUBBLES: ReadonlyArray<Bubble> = [];
const CUSTOM_EMOJI = /<(a?):([A-Za-z0-9_~-]{1,32}):(\d{1,20})>/g;
const SPOILER_MARKUP = /\|\|[\s\S]+?\|\|/;
const SENSITIVE_ATTACHMENT = MessageAttachmentFlags.IS_SPOILER | MessageAttachmentFlags.CONTAINS_EXPLICIT_MEDIA;
const BUBBLE_MESSAGE_TYPES: ReadonlySet<number> = new Set([MessageTypes.DEFAULT, MessageTypes.REPLY]);

function isSensitiveMedia(media: EmbedMedia | undefined): boolean {
	return media != null && (media.nsfw === true || (media.flags & EmbedMediaFlags.CONTAINS_EXPLICIT_MEDIA) !== 0);
}

function isSensitive(message: Message): boolean {
	if (SPOILER_MARKUP.test(message.content ?? '')) return true;
	for (const attachment of message.attachments ?? []) {
		if (attachment.nsfw || (attachment.flags & SENSITIVE_ATTACHMENT) !== 0) return true;
	}
	for (const embed of message.embeds ?? []) {
		if (embed.nsfw || isSensitiveMedia(embed.thumbnail) || isSensitiveMedia(embed.video)) return true;
		if (isSensitiveMedia(embed.image)) return true;
	}
	return false;
}

function isMediaProxyURL(url: string | undefined): url is string {
	const base = RuntimeConfig.mediaEndpoint;
	return url != null && base.length > 0 && url.startsWith(`${base.replace(/\/+$/, '')}/`);
}

function findGifv(embeds: ReadonlyArray<MessageEmbed> | undefined): MessageEmbed | null {
	return embeds?.find((embed) => embed.type === MessageEmbedTypes.GIFV) ?? null;
}

function gifOf(embed: MessageEmbed | null): Bubble['gif'] {
	const preview = embed?.thumbnail;
	if (!preview || !isMediaProxyURL(preview.proxy_url)) return null;
	const width = preview.width ?? embed?.video?.width ?? GIF_MAX_WIDTH;
	const height = preview.height ?? embed?.video?.height ?? GIF_MAX_HEIGHT;
	if (width <= 0 || height <= 0) return null;
	const scale = Math.min(1, GIF_MAX_WIDTH / width, GIF_MAX_HEIGHT / height);
	return {
		url: stripMediaProxyParams(preview.proxy_url),
		width: Math.max(1, Math.round(width * scale)),
		height: Math.max(1, Math.round(height * scale)),
	};
}

function customEmojis(content: string): Array<BubbleEmoji> {
	const found = new Map<string, BubbleEmoji>();
	for (const [, animated, name, id] of content.matchAll(CUSTOM_EMOJI)) {
		if (!found.has(id)) found.set(id, {id, name, animated: animated === 'a'});
	}
	return [...found.values()];
}

function clip(text: string): string {
	const chars = Array.from(text);
	if (chars.length <= ROOM_LIMITS.bubbleTextMax) return text;
	return `${chars.slice(0, ROOM_LIMITS.bubbleTextMax - 1).join('')}…`;
}

function contentOf(message: Message): Omit<Bubble, 'id' | 'expiresAt'> | null {
	if (isSensitive(message)) return {text: '', emojis: [], gif: null, spoiler: true};
	const gifv = findGifv(message.embeds);
	const gif = gifOf(gifv);
	let text = buildWireMessageNotificationBody(message, i18n).trim();
	if (gif && gifv?.url) text = text.split(gifv.url).join('').trim();
	if (!text && !gif) return null;
	return {text: clip(text), emojis: customEmojis(message.content ?? ''), gif, spoiler: false};
}

// Speech bubbles over the people in the open 3D room, fed by real channel messages. One sweep timer for all of them.
class VoiceSceneBubblesState {
	private channelId: string | null = null;
	private openedAt = 0;
	private isInRoom: (userId: string) => boolean = () => false;
	private shown: Array<Entry> = [];
	private queued: Array<Entry> = [];
	private sweepTimer: ReturnType<typeof setTimeout> | null = null;

	constructor() {
		makeAutoObservable<this, 'sweepTimer' | 'isInRoom'>(this, {sweepTimer: false, isInRoom: false}, {autoBind: true});
		ResettableStates.register(this, this.close);
	}

	open(channelId: string, openedAt: number, isInRoom: (userId: string) => boolean): void {
		this.close();
		this.channelId = channelId;
		this.openedAt = openedAt;
		this.isInRoom = isInRoom;
	}

	close(): void {
		this.channelId = null;
		this.isInRoom = () => false;
		this.shown = [];
		this.queued = [];
		this.schedule();
	}

	bubblesOf(userId: string): ReadonlyArray<Bubble> {
		const own = this.shown.filter((entry) => entry.authorId === userId);
		return own.length === 0 ? NO_BUBBLES : own.map((entry) => entry.bubble);
	}

	handleMessageCreate(message: Message): void {
		if (message.channel_id !== this.channelId) return;
		if (!BUBBLE_MESSAGE_TYPES.has(message.type) || message.webhook_id) return;
		const authorId = message.author?.id;
		if (!authorId || message.blocked || Relationships.isBlocked(authorId) || !this.isInRoom(authorId)) return;
		// A mature content channel shows its messages only past Fluxer's gate; the room shows no more than the chat.
		if (GuildMatureContentAgree.shouldShowGate({channelId: message.channel_id, guildId: message.guild_id ?? null}))
			return;
		const sentAt = Date.parse(message.timestamp);
		if (!Number.isFinite(sentAt) || sentAt < this.openedAt - OPENED_AT_SLACK_MS) return;
		if (this.has(message.id)) return;
		const content = contentOf(message);
		if (!content) return;
		const entry: Entry = {authorId, bubble: {id: message.id, ...content, expiresAt: 0}};
		const ownShown = this.shown.filter((shown) => shown.authorId === authorId).length;
		if (this.shown.length < ROOM_LIMITS.bubblesMax || ownShown >= BUBBLES_PER_PERSON) {
			this.show(entry, Date.now());
		} else {
			this.queued = [...this.queued, entry].slice(-ROOM_LIMITS.bubbleQueueMax);
		}
		this.schedule();
	}

	// An embed unfurled after the send (MESSAGE_UPDATE) or an edit refreshes the bubble; its time stays.
	handleMessageUpdate(message: Message): void {
		if (message.channel_id !== this.channelId || !this.has(message.id)) return;
		if (message.content === undefined && message.embeds === undefined) return;
		const content = contentOf(message);
		const refresh = (list: Array<Entry>): Array<Entry> =>
			content
				? list.map((entry) =>
						entry.bubble.id === message.id ? {...entry, bubble: {...entry.bubble, ...content}} : entry,
					)
				: list.filter((entry) => entry.bubble.id !== message.id);
		this.shown = refresh(this.shown);
		this.queued = refresh(this.queued);
		this.promote(Date.now());
		this.schedule();
	}

	handleMessageDelete(channelId: string, id: string): void {
		this.handleMessageDeleteBulk(channelId, [id]);
	}

	handleMessageDeleteBulk(channelId: string, ids: ReadonlyArray<string>): void {
		if (channelId !== this.channelId) return;
		const gone = new Set(ids);
		const keep = (entry: Entry) => !gone.has(entry.bubble.id);
		if (this.shown.every(keep) && this.queued.every(keep)) return;
		this.shown = this.shown.filter(keep);
		this.queued = this.queued.filter(keep);
		this.promote(Date.now());
		this.schedule();
	}

	private has(id: string): boolean {
		return this.shown.some((entry) => entry.bubble.id === id) || this.queued.some((entry) => entry.bubble.id === id);
	}

	// A person keeps at most BUBBLES_PER_PERSON; their newest drops their oldest at once.
	private show(entry: Entry, now: number): void {
		const own = this.shown.filter((shown) => shown.authorId === entry.authorId);
		const kept = own.length >= BUBBLES_PER_PERSON ? this.shown.filter((shown) => shown !== own[0]) : this.shown;
		this.shown = [...kept, {...entry, bubble: {...entry.bubble, expiresAt: now + BUBBLE_LIFETIME_MS}}];
	}

	private promote(now: number): void {
		while (this.shown.length < ROOM_LIMITS.bubblesMax && this.queued.length > 0) {
			const [next, ...rest] = this.queued;
			this.queued = rest;
			this.show(next, now);
		}
	}

	private sweep(): void {
		this.sweepTimer = null;
		const now = Date.now();
		this.shown = this.shown.filter((entry) => entry.bubble.expiresAt > now);
		this.promote(now);
		this.schedule();
	}

	private schedule(): void {
		if (this.sweepTimer !== null) {
			clearTimeout(this.sweepTimer);
			this.sweepTimer = null;
		}
		if (this.shown.length === 0) return;
		const next = Math.min(...this.shown.map((entry) => entry.bubble.expiresAt));
		this.sweepTimer = setTimeout(this.sweep, Math.max(0, next - Date.now()));
	}
}

export default new VoiceSceneBubblesState();
